import { useState, type FormEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import {
  api, ApiError, type Adjunto, type Caja, type Fideicomiso, type FiduciantePublico, type GastoDetalle, type Grupo, type Pago, type Proveedor, type Rubro,
  type TipoCambioSugerido,
} from "@/lib/api";
import { cajaPorDefecto, hayVariasCajas, opcionesCaja } from "@/lib/cajas";
import {
  aPayloadGasto,
  aPayloadPago,
  admiteCuentaCorriente,
  agregarAsignacion,
  cambiarPorcentajeAsignacion,
  feePrevisto,
  formGasto,
  formPago,
  montoAInput,
  partirPago,
  pagoParcialConFee,
  AVISO_FEE_AL_SALDAR,
  totalAPagar,
  totalDePago,
  totalesDePagos,
  mensajeBorrado,
  opcionesAsignacion,
  opcionesMedioPago,
  opcionesProveedor,
  quitarAsignacion,
  validarGasto,
  validarPago,
  type ErroresGasto,
  type ErroresPago,
  type FormGasto,
  type FormPago,
} from "@/lib/gastos";
import { MAX_ADJUNTOS, validarArchivo } from "@/lib/adjuntos";
import { opcionesRubro } from "@/lib/proveedores";
import { cambiarCampoFactura } from "@/lib/facturas";
import { useCrearProveedor } from "./useCrearProveedor";
import { useVinculosFactura } from "./useVinculosFactura";

type Dialogo =
  | { tipo: "editar" }
  | { tipo: "borrar" }
  | { tipo: "pago"; pago: Pago | null }
  | { tipo: "borrarPago"; pago: Pago }
  | null;

const SIN_ERRORES_GASTO: ErroresGasto = {};
const SIN_ERRORES_PAGO: ErroresPago = {};

/** Lógica de la ficha de un gasto (sin JSX): edición, baja, pagos y adjuntos. Sólo el admin escribe. */
export function useGastoTab() {
  const { fid, membresia } = useContextoApp();
  const { id } = useParams();
  const navegar = useNavigate();
  const gastoId = Number(id);
  const base = `/api/fideicomisos/${fid}/gastos`;
  const ruta = `${base}/${gastoId}`;
  const esAdmin = membresia.rol === "admin";

  const ficha = useApiResource<GastoDetalle>(ruta);
  const proveedores = useApiResource<Proveedor[]>(esAdmin ? `/api/fideicomisos/${fid}/proveedores?archivados=true` : null);
  const rubros = useApiResource<Rubro[]>(esAdmin ? `/api/fideicomisos/${fid}/rubros?archivados=true` : null);
  const fideicomiso = useApiResource<Fideicomiso>(esAdmin ? `/api/fideicomisos/${fid}` : null);
  const tcSugerido = useApiResource<TipoCambioSugerido>(esAdmin ? `/api/fideicomisos/${fid}/tipo-cambio-sugerido` : null);
  const cajas = useApiResource<Caja[]>(esAdmin ? `/api/fideicomisos/${fid}/cajas` : null);
  const gasto = ficha.data ?? null;
  // A quién se le puede acreditar un pago por cuenta corriente (sólo en un gasto B)
  const conCuentaCorriente = esAdmin && gasto !== null && admiteCuentaCorriente(gasto);
  const fiduciantes = useApiResource<FiduciantePublico[]>(conCuentaCorriente ? `/api/fideicomisos/${fid}/fiduciantes` : null);
  const grupos = useApiResource<Grupo[]>(conCuentaCorriente ? `/api/fideicomisos/${fid}/grupos` : null);

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [formG, setFormG] = useState<FormGasto>(() => formGasto());
  const [formP, setFormP] = useState<FormPago>(() => formPago());
  const vinculos = useVinculosFactura(fid, formG, esAdmin && dialogo?.tipo === "editar", gasto);
  const [mostrarErrores, setMostrarErrores] = useState(false);
  // Comprobantes elegidos en "Registrar pago": se suben después de crear el pago.
  const [comprobantes, setComprobantes] = useState<File[]>([]);
  const [errorComprobantes, setErrorComprobantes] = useState<string | null>(null);
  const [subiendoComprobantes, setSubiendoComprobantes] = useState(false);
  const [avisoComprobantes, setAvisoComprobantes] = useState<string | null>(null);
  /** Desde el alta de una factura escaneada, si no se pudo adjuntar el archivo. */
  const avisoAlta = (useLocation().state as { aviso?: string } | null)?.aviso ?? null;

  // Los delete devuelven 204 (undefined): se mapean a `true` para distinguir éxito de fallo en `run`.
  const { crearProveedor, errorCrearProveedor, limpiarErrorProveedor } = useCrearProveedor(fid, proveedores.recargar, (id) =>
    setFormG((f) => cambiarCampoFactura(f, "proveedor_id", id, null)),
  );
  const accionEditar = useAsyncAction((datos: ReturnType<typeof aPayloadGasto>) => api.patch<GastoDetalle>(ruta, datos));
  const accionBorrar = useAsyncAction(() => api.delete<void>(ruta).then(() => true));
  const accionPago = useAsyncAction((pagoId: number | null, datos: ReturnType<typeof aPayloadPago>) =>
    pagoId === null ? api.post<Pago>(`${ruta}/pagos`, datos) : api.patch<Pago>(`${ruta}/pagos/${pagoId}`, datos),
  );
  const accionBorrarPago = useAsyncAction((pagoId: number) => api.delete<void>(`${ruta}/pagos/${pagoId}`).then(() => true));
  const accionSubir = useAsyncAction((destino: string, archivo: File) => {
    const form = new FormData();
    form.append("archivo", archivo);
    return api.upload<Adjunto>(destino, form);
  });
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const erroresG = validarGasto(formG);
  // el monto del pago es el total con fee (Mati, 2026-11-02): al editar, lo del propio pago vuelve a estar disponible
  const montoEditado = dialogo?.tipo === "pago" ? dialogo.pago?.monto : undefined;
  const erroresP = gasto ? validarPago(formP, gasto.moneda, gasto, montoEditado) : SIN_ERRORES_PAGO;
  const feePct = fideicomiso.data?.fee_porcentaje ?? null;
  const cerrar = () => setDialogo(null);
  const recargar = () => {
    cerrar();
    ficha.recargar();
  };

  const abrirEditar = () => {
    if (!gasto) return;
    setFormG(formGasto(gasto));
    limpiarErrorProveedor();
    setMostrarErrores(false);
    accionEditar.limpiarError();
    setDialogo({ tipo: "editar" });
  };

  const guardarGasto = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (Object.keys(erroresG).length > 0) return;
    if (await accionEditar.run(aPayloadGasto(formG))) recargar();
  };

  const confirmarBorrar = async () => {
    if (await accionBorrar.run()) void navegar("/gastos");
  };

  const abrirPago = (pago: Pago | null) => {
    const form = formPago(pago ?? undefined, undefined, tcSugerido.data?.tipo_cambio, cajaPorDefecto(cajas.data ?? []), gasto?.cuenta_corriente_sugerida);
    // de entrada, todo lo que falta con el fee (o el total del pago que se edita)
    if (gasto) form.monto = montoAInput(pago ? totalDePago(gasto, gasto.pagos, pago) : totalAPagar(gasto));
    setFormP(form);
    setMostrarErrores(false);
    accionPago.limpiarError();
    setComprobantes([]);
    setErrorComprobantes(null);
    setAvisoComprobantes(null);
    setDialogo({ tipo: "pago", pago });
  };

  const guardarPago = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (!gasto || dialogo?.tipo !== "pago" || Object.keys(erroresP).length > 0) return;
    const pago = await accionPago.run(dialogo.pago?.id ?? null, aPayloadPago(formP, gasto.moneda, gasto, montoEditado));
    if (!pago) return;
    if (dialogo.pago === null && comprobantes.length > 0) setAvisoComprobantes(await subirComprobantes(pago.id));
    recargar();
  };

  /** Sube los comprobantes elegidos al pago recién creado. El pago ya quedó guardado: si alguno falla, devuelve el aviso. */
  const subirComprobantes = async (pagoId: number): Promise<string | null> => {
    setSubiendoComprobantes(true);
    try {
      for (const archivo of comprobantes) {
        const form = new FormData();
        form.append("archivo", archivo);
        await api.upload<Adjunto>(`${ruta}/pagos/${pagoId}/adjuntos`, form);
      }
      return null;
    } catch (e) {
      const motivo = e instanceof ApiError ? e.message : "Ocurrió un error inesperado";
      return `El pago se guardó, pero no se pudo subir el comprobante (${motivo}). Subilo desde "Comprobantes del pago".`;
    } finally {
      setSubiendoComprobantes(false);
    }
  };

  const elegirComprobantes = (archivos: File[]) => {
    const error = archivos.map((a) => validarArchivo(a)).find(Boolean) ?? null;
    if (!error && comprobantes.length + archivos.length > MAX_ADJUNTOS) {
      setErrorComprobantes(`Hasta ${MAX_ADJUNTOS} comprobantes por pago`);
      return;
    }
    setErrorComprobantes(error);
    if (!error) setComprobantes((c) => [...c, ...archivos]);
  };

  const confirmarBorrarPago = async () => {
    if (dialogo?.tipo !== "borrarPago") return;
    if (await accionBorrarPago.run(dialogo.pago.id)) recargar();
  };

  const subir = async (destino: string, archivo: File) => {
    accionBorrarAdjunto.limpiarError();
    if (await accionSubir.run(destino, archivo)) ficha.recargar();
  };

  const borrarAdjunto = async (a: Adjunto) => {
    accionSubir.limpiarError();
    if (await accionBorrarAdjunto.run(a.id)) ficha.recargar();
  };

  const partesPago = gasto && dialogo?.tipo === "pago" ? partirPago(formP.monto, gasto, montoEditado) : null;

  return {
    esAdmin,
    /** El fiduciante ve el gasto con su fee sumado (Mati, 2026-10-20). */
    sumarFee: membresia.rol === "fiduciante",
    /** Lo que falta pagar con el fee y, en el diálogo, cómo se parte el total escrito (Mati, 2026-11-02). */
    totalAPagar: gasto ? totalAPagar(gasto) : null,
    partesPago,
    /** Pago parcial de un gasto con fee: va todo al proveedor y el fee se genera al saldar (Mati, 2026-11-02). */
    avisoFee: gasto && pagoParcialConFee(partesPago, gasto) ? AVISO_FEE_AL_SALDAR : null,
    totalesPagos: gasto ? totalesDePagos(gasto, gasto.pagos) : new Map<number, { total: string; fee: string }>(),
    fid,
    gasto,
    // De qué caja salió la plata (Mati, 2026-10-19); con una sola caja activa no se pregunta: va a la principal.
    opcionesCajaPago: hayVariasCajas(cajas.data ?? []) ? opcionesCaja(cajas.data ?? [], dialogo?.tipo === "pago" ? (dialogo.pago?.caja_id ?? null) : null) : null,
    cargando: ficha.loading && !ficha.data,
    errorCarga: ficha.data ? null : (ficha.error?.message ?? null),
    dialogo,
    cerrar,
    // gasto
    abrirEditar,
    formG,
    cambiarG: <K extends keyof FormGasto>(campo: K, valor: FormGasto[K]) => setFormG((f) => cambiarCampoFactura(f, campo, valor, vinculos.vinculables)),
    erroresG: { ...(mostrarErrores ? erroresG : SIN_ERRORES_GASTO), ...(errorCrearProveedor ? { proveedor_id: errorCrearProveedor } : {}) },
    crearProveedor,
    opcionesProveedorForm: opcionesProveedor(proveedores.data ?? [], gasto?.proveedor?.id),
    opcionesRubroForm: opcionesRubro((rubros.data ?? []).filter((r) => r.clave !== "fee"), gasto?.rubro.id),
    opcionesPresupuesto: vinculos.opcionesPresupuesto,
    opcionesCuota: vinculos.opcionesCuota,
    opcionesItem: vinculos.opcionesItem,
    // Al editar, el fee previsto usa el % congelado del gasto (si lo tiene); si no, el vigente.
    feePrevisto: feePct && formG.lleva_fee ? feePrevisto(formG.monto, gasto?.fee_porcentaje ?? feePct) : null,
    feePorcentaje: gasto?.fee_porcentaje ?? feePct,
    guardarGasto,
    guardando: accionEditar.loading,
    errorGuardar: accionEditar.error,
    abrirBorrar: () => {
      accionBorrar.limpiarError();
      setDialogo({ tipo: "borrar" });
    },
    mensajeBorrado: gasto ? mensajeBorrado(gasto) : "",
    confirmarBorrar,
    borrado: accionBorrar,
    // pagos
    abrirPago,
    formP,
    cambiarP: <K extends keyof FormPago>(campo: K, valor: FormPago[K]) => setFormP((f) => ({ ...f, [campo]: valor })),
    erroresP: mostrarErrores ? erroresP : SIN_ERRORES_PAGO,
    opcionesMedio: gasto ? opcionesMedioPago(gasto) : [],
    porCuentaCorriente: formP.medio === "cuenta_corriente",
    opcionesAsignacion: opcionesAsignacion(fiduciantes.data ?? [], grupos.data ?? [], formP.asignaciones),
    agregarAsignacion: (clave: string) =>
      setFormP((f) => ({ ...f, asignaciones: agregarAsignacion(f.asignaciones, clave, fiduciantes.data ?? [], grupos.data ?? []) })),
    quitarAsignacion: (clave: string) => setFormP((f) => ({ ...f, asignaciones: quitarAsignacion(f.asignaciones, clave) })),
    cambiarPorcentajeAsignacion: (clave: string, valor: string) =>
      setFormP((f) => ({ ...f, asignaciones: cambiarPorcentajeAsignacion(f.asignaciones, clave, valor) })),
    guardarPago,
    guardandoPago: accionPago.loading || subiendoComprobantes,
    comprobantes,
    elegirComprobantes,
    quitarComprobante: (i: number) => setComprobantes((c) => c.filter((_, j) => j !== i)),
    errorComprobantes,
    avisoComprobantes,
    avisoAlta,
    errorPago: accionPago.error,
    abrirBorrarPago: (pago: Pago) => {
      accionBorrarPago.limpiarError();
      setDialogo({ tipo: "borrarPago", pago });
    },
    confirmarBorrarPago,
    borrandoPago: accionBorrarPago.loading,
    errorBorrarPago: accionBorrarPago.error,
    // adjuntos
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    subirAlGasto: (archivo: File) => subir(`${ruta}/adjuntos`, archivo),
    subirAlPago: (pago: Pago) => (archivo: File) => subir(`${ruta}/pagos/${pago.id}/adjuntos`, archivo),
    borrarAdjunto,
    subiendo: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error,
  };
}
